<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.questions', 2),
                        href: meta.links.questions_index,
                    },
                    {
                        name: $tc('messages.question_reviews', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.subject', 2)" for="subject_id" />
                        <FormSelect
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="options.subjects"
                            class="mt-1 w-full"
                            :null-option="true"
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$t('messages.status')" for="status" />
                        <FormSelect id="status" v-model="form.status" :options="options.statuses" class="mt-1 w-full" />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="questions.data.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>

        <div class="mt-5" v-else>
            <div
                v-for="question in questions.data"
                class="rounded-lg border-2 border-gray-300 py-2 px-3 shadow-lg"
                :key="`${question.id}_${question.updated_at}`"
            >
                <AppWell>
                    <div class="md:grid md:grid-cols-2 md:gap-6 mt-5">
                        <AppCard>
                            <QuestionBaseDisplay :question="question">
                                <div>
                                    <div class="flex justify-between p-1">
                                        <div class="inline-block">
                                            <QuestionEditFormPanel
                                                is-review
                                                :question="question"
                                                v-if="question.original_id === null"
                                            />
                                        </div>
                                        <div class="inline-block">
                                            <InertiaSquareLinkButton action="show" :href="question.links.show" />
                                        </div>
                                    </div>
                                    <div class="font-semibold text-gray-900 truncate">
                                        {{ question.title }}
                                    </div>
                                    <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.description">
                                        <QuestionDescriptionDisplay :question="question" />
                                    </div>
                                    <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.image_path">
                                        <img :src="question.image_path" alt="cover" class="h-auto" />
                                    </div>
                                    <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.shared_image">
                                        <img :src="question.shared_image" alt="cover" class="h-auto" />
                                    </div>
                                    <div>
                                        <CopyrightNotice :info="question.copyright" />
                                    </div>
                                    <div class="mt-3 p-2" v-if="question.graph">
                                        <QuestionLineGraph
                                            :data="question.graph"
                                            v-if="question.graph.type === 'R1Did'"
                                        />
                                        <GraphToolbox
                                            :elements="question.graph.elements"
                                            :graph="question.graph.graph"
                                            v-if="question.graph.type === 'GraphToolbox'"
                                        />
                                        <GraphFunctionPlot
                                            :elements="question.graph.elements"
                                            :graph="question.graph.graph"
                                            v-if="question.graph.type === 'GraphFunctionPlot'"
                                        />
                                    </div>

                                    <HintAlert v-if="question.hint" class="mt-1">
                                        <span class="font-semibold">{{ $t('messages.hint') }}:</span>
                                        {{ question.hint }}
                                    </HintAlert>

                                    <component :is="question.type" :question="question" />
                                </div>
                                <div class="mt-1 flex flex-wrap gap-2">
                                    <InertiaLink v-for="tag in question.tag_links" :key="tag.id" :href="tag.url">
                                        <BadgePill>
                                            {{ tag.name }}
                                        </BadgePill>
                                    </InertiaLink>
                                </div>
                                <div class="mt-1 flex flex-wrap gap-2">
                                    <BadgePill
                                        v-for="grade_level in question.grade_levels_display"
                                        :key="grade_level.id"
                                        color="gray"
                                    >
                                        {{ grade_level.name }}
                                    </BadgePill>
                                </div>
                                <div v-if="question.answers_count">
                                    <BadgePill color="gray">
                                        {{ $tc('messages.answers', 2) }}: {{ question.answers_count }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <BadgePill color="gray">
                                        {{ question.subject_name }}
                                    </BadgePill>
                                </div>
                            </QuestionBaseDisplay>
                        </AppCard>
                        <div>
                            <div v-for="review in question.question_reviews" :key="review.id">
                                <AppCard>
                                    <div>
                                        <div>{{ review.comment }}</div>
                                        <div class="text-xs text-gray-500" :title="review.created_at_display">
                                            {{ review.time_ago }}
                                        </div>
                                        <div class="flex justify-end gap-2 text-sm">
                                            <div>{{ review.user.full_name }}</div>
                                            <div class="text-gray-500">({{ review.user.type.display }})</div>
                                        </div>
                                        <div class="flex justify-end text-xs text-gray-500">
                                            <div :title="$tc('messages.schools', 1)">
                                                <IconBuilding class="w-4 h-4" />
                                            </div>
                                            <div>
                                                {{ review.user.school_name }}
                                            </div>
                                        </div>
                                    </div>
                                </AppCard>
                            </div>
                        </div>
                    </div>
                </AppWell>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="questions" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import QuestionEditFormPanel from '@/components/Quiz/QuestionEditFormPanel.vue';
import HintAlert from '@/components/Quiz/HintAlert.vue';
import BadgePill from '@/components/BadgePill.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import { IconBuilding, IconCircleXFilled } from '@tabler/icons-vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import DataListSuggestions from '@/components/Quiz/DataListSuggestions.vue';
import QuestionBaseDisplay from '@/components/Quiz/QuestionBaseDisplay.vue';
import CopyrightNotice from '@/components/CopyrightNotice.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import Modal from '@/components/Modal.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import GraphToolbox from '../../../../components/Quiz/GraphToolbox.vue';
import GraphFunctionPlot from '../../../../components/Quiz/GraphFunctionPlot.vue';
import QuestionLineGraph from '../../../../components/Quiz/QuestionLineGraph.vue';

export default {
    components: {
        QuestionLineGraph,
        GraphFunctionPlot,
        GraphToolbox,
        AppCard,
        AppWell,
        Modal,
        SlideOverCheckboxInput,
        CopyrightNotice,
        QuestionBaseDisplay,
        DataListSuggestions,
        SubmitFormButton,
        AppButton,
        EmptyState,
        FormError,
        FormLabel,
        FormSelect,
        QuestionDescriptionDisplay,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        QuestionEditFormPanel,
        HintAlert,
        BadgePill,
        IconCircleXFilled,
        IconBuilding,

        // { question_view_only_component }
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'questions', 'filters', 'options', 'defaults'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                status: this.filters.status,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
